import { useCallback, useEffect, useState } from 'react';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Building2, ChevronDown, Trash2 } from 'lucide-react';

const SHARED_ROLES = ['super_user', 'hr_payroll'];
const COMPANIES = [{ key: 'thrive', label: 'Thrive' }, { key: 'ludo', label: 'Ludo ABA' }];
const defaultOrgs = (u) => {
  if (Array.isArray(u.orgs) && u.orgs.length) return u.orgs;
  return (u.email || '').toLowerCase().endsWith('@ludoaba.com') ? ['ludo'] : ['thrive'];
};

function CompanyAccess({ user, onSave }) {
  if (SHARED_ROLES.includes(user.role)) {
    return <span className="text-[11px] text-[#685B72]" data-testid={`orgs-${user.user_id}`}>Both (by role)</span>;
  }
  const cur = defaultOrgs(user);
  const toggle = (k) => {
    const next = cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k];
    if (!next.length) { toast.error('Pick at least one company'); return; }
    onSave(next);
  };
  return (
    <div className="flex gap-3" data-testid={`orgs-${user.user_id}`}>
      {COMPANIES.map((c) => (
        <label key={c.key} className="flex items-center gap-1.5 text-[12px] text-[#2D2833] cursor-pointer">
          <Checkbox checked={cur.includes(c.key)} onCheckedChange={() => toggle(c.key)} data-testid={`org-${c.key}-${user.user_id}`} />
          {c.label}
        </label>
      ))}
    </div>
  );
}
import ConfirmDestructive from '../components/ConfirmDestructive';

const ROLES = [
  { value: 'super_user', label: 'Super User' },
  { value: 'admin', label: 'Admin' },
  { value: 'office_director', label: 'Office Director' },
  { value: 'hr_payroll', label: 'HR / Payroll' },
  { value: 'data_import', label: 'Data Import Agent' },
  { value: 'read_only', label: 'Read Only' },
  { value: 'pending', label: 'Pending Approval' },
];

const CLINICS = ['Katy', 'Missouri City', 'Townelake', 'Fry Rd', 'Memorial', 'Brookshire', 'Tomball', 'Magnolia', 'Atascocita'];

function ClinicMultiSelect({ user, onSave }) {
  const initial = user.clinics?.length ? user.clinics : (user.clinic ? [user.clinic] : []);
  const [selected, setSelected] = useState(initial);
  const [open, setOpen] = useState(false);

  const toggle = (name) => {
    setSelected(cur => cur.includes(name) ? cur.filter(c => c !== name) : [...cur, name]);
  };

  const summary =
    selected.length === 0 ? '— None (all clinics)'
    : selected.length === CLINICS.length ? 'All clinics'
    : selected.length <= 2 ? selected.join(', ')
    : `${selected.length} clinics`;

  const apply = () => {
    onSave(selected);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-[220px] h-8 justify-between rounded-lg text-xs font-normal" data-testid={`clinics-picker-${user.user_id}`}>
          <span className="truncate flex items-center gap-2"><Building2 size={13} /> {summary}</span>
          <ChevronDown size={12} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" data-testid={`clinics-menu-${user.user_id}`}>
        <div className="text-xs text-[#685B72] mb-2">Grant access to:</div>
        <div className="space-y-1.5 max-h-72 overflow-y-auto">
          {CLINICS.map(c => (
            <label key={c} className="flex items-center gap-2 text-sm px-2 py-1.5 rounded-md hover:bg-[#F1EDF5] cursor-pointer">
              <Checkbox checked={selected.includes(c)} onCheckedChange={() => toggle(c)} data-testid={`clinic-cb-${user.user_id}-${c}`} />
              <span className="text-[#2D2833]">{c}</span>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-3 pt-3 border-t border-[#E8E5EC]">
          <Button size="sm" variant="ghost" onClick={() => { setSelected(initial); setOpen(false); }}>Cancel</Button>
          <Button size="sm" onClick={apply} className="bg-[#685B72] hover:bg-[#52475A] text-white" data-testid={`clinics-apply-${user.user_id}`}>Apply</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Users() {
  const [users, setUsers] = useState([]);
  const [me, setMe] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteRunning, setDeleteRunning] = useState(false);
  const load = useCallback(() => api.get('/users').then(r => setUsers(r.data)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // Grab current user so we can hide the Delete button on our own row.
    api.get('/auth/me').then(r => setMe(r.data)).catch(() => {});
  }, []);

  const update = async (id, patch) => {
    try {
      await api.patch(`/users/${id}`, patch);
      toast.success('Updated');
      load();
    } catch (e) { toast.error('Update failed'); }
  };

  const removeUser = async () => {
    if (!pendingDelete) return;
    const u = pendingDelete;
    const label = `${u.name || u.email} (${u.email})`;
    setDeleteRunning(true);
    try {
      await api.delete(`/users/${u.user_id}`);
      toast.success(`Deleted ${label}`);
      setPendingDelete(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleteRunning(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-[#685B72]/70 font-semibold">Admin</div>
        <h1 className="font-serif text-4xl text-[#2D2833] mt-1">User Management</h1>
        <p className="text-[#685B72] mt-1">Approve new sign-ins, assign roles, and grant access to one or many clinics.</p>
      </div>

      <Card className="rounded-2xl border-[#E8E5EC] p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#FAF8FB] text-[#685B72] text-left">
                <th className="px-4 py-3 font-semibold">User</th>
                <th className="px-4 py-3 font-semibold">Email</th>
                <th className="px-4 py-3 font-semibold">Role</th>
                <th className="px-4 py-3 font-semibold">Clinics</th>
                <th className="px-4 py-3 font-semibold">Companies</th>
                <th className="px-4 py-3 font-semibold">Active</th>
                <th className="px-4 py-3 font-semibold w-24 text-right">Delete</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const isSelf = me && me.user_id === u.user_id;
                return (
                  <tr key={u.user_id} className="border-t border-[#E8E5EC]" data-testid={`user-row-${u.user_id}`}>
                    <td className="px-4 py-3 text-[#2D2833] font-medium">{u.name}</td>
                    <td className="px-4 py-3 text-[#685B72]">{u.email}</td>
                    <td className="px-4 py-3">
                      <Select value={u.role} onValueChange={v => update(u.user_id, { role: v })}>
                        <SelectTrigger className="w-[160px] h-8 rounded-lg" data-testid={`role-${u.user_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3">
                      {!SHARED_ROLES.includes(u.role) && defaultOrgs(u).length === 1 && defaultOrgs(u)[0] === 'ludo'
                        ? <span className="text-[11px] text-[#685B72]" data-testid={`clinics-ludo-${u.user_id}`}>Ludo ABA</span>
                        : <ClinicMultiSelect user={u} onSave={(list) => update(u.user_id, { clinics: list })} />}
                    </td>
                    <td className="px-4 py-3">
                      <CompanyAccess user={u} onSave={(orgs) => update(u.user_id, { orgs })} />
                    </td>
                    <td className="px-4 py-3"><Switch checked={u.active} onCheckedChange={v => update(u.user_id, { active: v })} data-testid={`active-${u.user_id}`} /></td>
                    <td className="px-4 py-3 text-right">
                      {isSelf ? (
                        <span className="text-[10px] text-[#685B72] italic">you</span>
                      ) : (
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => setPendingDelete(u)}
                          className="text-[#8A3A3A] hover:text-[#8A3A3A] hover:bg-[#F5EAEA]"
                          data-testid={`user-delete-${u.user_id}`}
                          title={`Delete ${u.email}`}
                        >
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-[#685B72]">No users yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
      <ConfirmDestructive
        open={!!pendingDelete}
        onOpenChange={(o) => { if (!o) setPendingDelete(null); }}
        tone="danger"
        title="Delete user"
        description={
          pendingDelete ? (
            <>
              Removes <b>{pendingDelete.name || pendingDelete.email}</b> (<span className="font-mono">{pendingDelete.email}</span>),
              their active sessions, and their personal preferences. They'll have to sign in again
              and re-request access before they can use the app.
            </>
          ) : null
        }
        confirmPhrase={pendingDelete?.email || ''}
        confirmHint={<>Type the user's email <span className="font-mono font-semibold">{pendingDelete?.email}</span> to enable Delete.</>}
        confirmLabel="Delete user"
        running={deleteRunning}
        onConfirm={removeUser}
        testId="confirm-delete-user"
      />
    </div>
  );
}
